import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utils/currencyFormatter';
import './StatisticsDisplay.scss';

const StatisticsDisplay = ({ data, detailedData }) => {
  const { t } = useTranslation(['backoffice']);

  // Transformar datos del endpoint a la estructura esperada
  const transformData = (apiData, detailedApiData) => {
    const basic = apiData?.basic_statistics || {};
    const detailed = detailedApiData || {};

    return {
      total_articles: basic.total_articles ?? 0,
      total_amount_selected: basic.total_amount ?? 0,
      percentage_discontinued: basic.out_of_stock_percentage ?? 0,
      percentage_served: basic.in_stock_percentage ?? 0,
      importe_total_facturado: detailed.invoiced_amount ?? 0,
      importe_pendientes_enviar: detailed.pending_to_ship_amount ?? 0,
      importe_total_provisional: detailed.provisional_amount ?? 0,
      presupuesto_restante_definitivo: detailed.final_budget_remaining ?? 0,
      presupuesto_restante: detailed.provisional_budget_remaining ?? 0,
      percentage_discontinued_pedido: detailed.out_of_stock_percentage ?? 0,
      percentage_served_pedido: detailed.served_percentage ?? 0,
      library_budget: basic.total_budget ?? 0
    };
  };

  const statistics = transformData(data, detailedData);

  const formatPercentage = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return '0.00';
    }
    return numValue.toFixed(2);
  };

  return (
    <div className="statistics-display">
      <div className="statistics-display__separator"></div>
      {/* Total de artículos */}
      <div className="statistics-display__row statistics-display__row--white">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.totalArticles', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {statistics.total_articles || 0}
          </Col>
        </Row>
      </div>

      {/* Importe total de los seleccionados(dcto. aplicado) */}
      <div className="statistics-display__row statistics-display__row--gray">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.totalAmountSelected', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatCurrency(statistics.total_amount_selected || 0)}
          </Col>
        </Row>
      </div>

      {/* Porcentaje de artículos descatalogados respecto a los seleccionados */}
      <div className="statistics-display__row statistics-display__row--white">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.percentageDiscontinued', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatPercentage(statistics.percentage_discontinued)} %
          </Col>
        </Row>
      </div>

      {/* Porcentaje de artículos servidos respecto a los seleccionados */}
      <div className="statistics-display__row statistics-display__row--gray">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.percentageServed', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatPercentage(statistics.percentage_served)} %
          </Col>
        </Row>
      </div>

      {/* Notas explicativas */}
      <div className="statistics-display__notes">
        <div className="statistics-display__note">
          <span className="statistics-display__note-asterisk">*</span>
          <span className="statistics-display__note-text">
            {t('statistics.noteAsterisk', { ns: 'backoffice' })}
          </span>
        </div>
        <div className="statistics-display__note">
          <span className="statistics-display__note-asterisk">**</span>
          <span className="statistics-display__note-text">
            {t('statistics.noteDoubleAsterisk', { ns: 'backoffice' })}
          </span>
        </div>
      </div>

      <div className="statistics-display__separator"></div>

      {/* Importe total facturado* */}
      <div className="statistics-display__row statistics-display__row--white">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.importeTotalFacturado', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatCurrency(statistics.importe_total_facturado || 0)}
          </Col>
        </Row>
      </div>

      {/* Importe total de los artículos solicitados y recibidos que están pendientes de enviar */}
      <div className="statistics-display__row statistics-display__row--gray">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.importePendientesEnviar', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatCurrency(statistics.importe_pendientes_enviar || 0)}
          </Col>
        </Row>
      </div>

      {/* Importe total provisional** */}
      <div className="statistics-display__row statistics-display__row--white">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.importeTotalProvisional', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatCurrency(statistics.importe_total_provisional || 0)}
          </Col>
        </Row>
      </div>

      {/* Presupuesto restante definitivo* */}
      <div className="statistics-display__row statistics-display__row--gray">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.presupuestoRestanteDefinitivo', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatCurrency(statistics.presupuesto_restante_definitivo || 0)}
          </Col>
        </Row>
      </div>

      {/* Presupuesto restante** */}
      <div className="statistics-display__row statistics-display__row--white">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.presupuestoRestante', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatCurrency(statistics.presupuesto_restante || 0)}
          </Col>
        </Row>
      </div>

      {/* Porcentaje de artículos descatalogados respecto al pedido */}
      <div className="statistics-display__row statistics-display__row--gray">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.percentageDiscontinuedPedido', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatPercentage(statistics.percentage_discontinued_pedido)}%
          </Col>
        </Row>
      </div>

      {/* Porcentaje de artículos servidos respecto al pedido */}
      <div className="statistics-display__row statistics-display__row--white">
        <Row className="statistics-display__item">
          <Col className="statistics-display__label">
            {t('statistics.percentageServedPedido', { ns: 'backoffice' })}
          </Col>
          <Col className="statistics-display__value">
            {formatPercentage(statistics.percentage_served_pedido)}%
          </Col>
        </Row>
      </div>

      {/* TOTALES BIBLIOTECA */}
      <div className="statistics-display__section">
        <div className="statistics-display__section-title">
          {t('statistics.libraryTotals', { ns: 'backoffice' })}
        </div>
        <div className="statistics-display__budget-container">
          <div className="statistics-display__budget-value">
            {formatCurrency(statistics.library_budget || 0)}
          </div>
          <div className="statistics-display__budget-label">
            {t('statistics.budget', { ns: 'backoffice' })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsDisplay;
